import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import { PHONE, PHONE_TEL, NAP_NAME, NAP_ADDRESS } from "@/components/PublicLayout";
import { api, apiError } from "@/lib/api";

const EMPTY = { name: "", email: "", phone: "", address: "", message: "", renovation_start: "", hire_duration: "", project_type: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you! Your enquiry has been sent — we'll be in touch shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full px-4 py-2.5 border border-slate-300 rounded text-base text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#1A237E] focus:border-transparent";

  return (
    <div data-testid="contact-page">
      <SEO
        title="Contact Us | Get a Free Quote — Mobile Kitchen Hire Surrey"
        description="Get a free quote for mobile kitchen hire Surrey. Call 07740051799 or send us a message. Temporary kitchen hire across Surrey from our Weybridge base."
      />
      <section className="bg-[#F8FAFC] border-b border-slate-200 py-14">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl sm:text-5xl font-bold text-[#1A237E]">Get a Free Quote</h1>
          <p className="mt-5 text-lg text-slate-600 max-w-3xl leading-relaxed">
            Tell us about your project and we'll come back with a clear, competitive quote for mobile kitchen
            hire in Surrey — no obligation, no hidden extras.
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-5 gap-12">
          <form onSubmit={submit} className="lg:col-span-3 space-y-5" data-testid="contact-form">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-semibold text-[#1A237E] mb-1.5">Name *</label>
                <input id="contact-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Your full name" />
              </div>
              <div>
                <label htmlFor="contact-phone" className="block text-sm font-semibold text-[#1A237E] mb-1.5">Phone *</label>
                <input id="contact-phone" data-testid="contact-phone-input" required type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="Your phone number" />
              </div>
            </div>
            <div>
              <label htmlFor="contact-email" className="block text-sm font-semibold text-[#1A237E] mb-1.5">Email *</label>
              <input id="contact-email" data-testid="contact-email-input" required type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="you@example.com" />
            </div>
            <div>
              <label htmlFor="contact-address" className="block text-sm font-semibold text-[#1A237E] mb-1.5">Address</label>
              <input id="contact-address" data-testid="contact-address-input" value={form.address} onChange={set("address")} className={inputCls} placeholder="Street, town, postcode" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-renovation-start" className="block text-sm font-semibold text-[#1A237E] mb-1.5">When does your renovation start?</label>
                <input id="contact-renovation-start" data-testid="contact-renovation-start-input" type="date" value={form.renovation_start} onChange={set("renovation_start")} className={inputCls} />
              </div>
              <div>
                <label htmlFor="contact-hire-duration" className="block text-sm font-semibold text-[#1A237E] mb-1.5">How long do you need the unit?</label>
                <select id="contact-hire-duration" data-testid="contact-hire-duration-select" value={form.hire_duration} onChange={set("hire_duration")} className={inputCls}>
                  <option value="">Select…</option>
                  <option value="2–4 weeks">2–4 weeks</option>
                  <option value="1–2 months">1–2 months</option>
                  <option value="2–4 months">2–4 months</option>
                  <option value="4+ months">4+ months</option>
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="contact-project-type" className="block text-sm font-semibold text-[#1A237E] mb-1.5">Type of project</label>
              <select id="contact-project-type" data-testid="contact-project-type-select" value={form.project_type} onChange={set("project_type")} className={inputCls}>
                <option value="">Select…</option>
                <option value="Kitchen renovation">Kitchen renovation</option>
                <option value="Water damage repair">Water damage repair</option>
                <option value="Building works">Building works</option>
                <option value="Insurance claim">Insurance claim</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label htmlFor="contact-message" className="block text-sm font-semibold text-[#1A237E] mb-1.5">Message</label>
              <textarea id="contact-message" data-testid="contact-message-input" rows={5} value={form.message} onChange={set("message")} className={inputCls} placeholder="Tell us about your project, likely dates and how long you'll need the kitchen…" />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-button"
              className="flex items-center gap-2 px-7 py-3.5 bg-[#1A237E] text-white font-semibold rounded hover:bg-[#121A5C] transition-colors disabled:opacity-60"
            >
              <Send className="h-4 w-4" /> {sending ? "Sending…" : "Send Enquiry"}
            </button>
          </form>

          <aside className="lg:col-span-2 space-y-6">
            <div className="bg-[#1A237E] rounded p-7 text-white" data-testid="contact-info-card">
              <h2 className="text-xl font-semibold">Prefer to Talk?</h2>
              <p className="mt-2 text-sm text-slate-300">Call us directly for friendly advice and a quick quote.</p>
              <a
                href={PHONE_TEL}
                data-testid="contact-call-button"
                className="mt-5 flex items-center justify-center gap-2 w-full px-6 py-3.5 bg-white text-[#1A237E] font-semibold rounded hover:bg-slate-100 transition-colors"
              >
                <Phone className="h-5 w-5" /> Call {PHONE}
              </a>
              <div className="mt-7 space-y-4 text-sm text-slate-200">
                <p className="flex gap-3">
                  <Mail className="h-5 w-5 shrink-0 text-white" />
                  <a href="mailto:enquiries@mobilekitchencompany.co.uk" className="hover:underline" data-testid="contact-email-link">
                    enquiries@mobilekitchencompany.co.uk
                  </a>
                </p>
                <p className="flex gap-3">
                  <MapPin className="h-5 w-5 shrink-0 text-white" />
                  <span>{NAP_NAME}<br />{NAP_ADDRESS}</span>
                </p>
                <p className="flex gap-3">
                  <Clock className="h-5 w-5 shrink-0 text-white" />
                  <span>Monday – Friday: 09:00 – 17:00<br />Saturday & Sunday: Closed</span>
                </p>
              </div>
            </div>
            <div className="border border-slate-200 rounded p-6 bg-[#F8FAFC]">
              <h3 className="text-lg font-semibold text-[#1A237E]">What Happens Next?</h3>
              <ol className="mt-3 space-y-2 text-sm text-slate-600 list-decimal list-inside">
                <li>We'll review your enquiry and give you a call.</li>
                <li>You'll receive a clear, no-obligation quote.</li>
                <li>We agree dates — then deliver, connect and set up your kitchen.</li>
              </ol>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
